import React, { useState, useRef, useEffect } from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { ChevronDown, Check } from 'lucide-react'

function cn(...inputs: (string | undefined | null | boolean)[]) {
  return twMerge(clsx(inputs))
}

interface SelectContextValue {
  value: string
  label: string
  onValueChange: (value: string, label: string) => void
  open: boolean
  setOpen: (open: boolean) => void
}

const SelectContext = React.createContext<SelectContextValue>({
  value: '',
  label: '',
  onValueChange: () => {},
  open: false,
  setOpen: () => {},
})

export interface SelectProps {
  value?: string
  onValueChange?: (value: string) => void
  defaultValue?: string
  children: React.ReactNode
}

export function Select({ value, onValueChange, defaultValue = '', children }: SelectProps) {
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [label, setLabel] = useState('')
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const currentValue = value !== undefined ? value : internalValue

  const handleValueChange = (newValue: string, newLabel: string) => {
    if (value === undefined) setInternalValue(newValue)
    setLabel(newLabel)
    onValueChange?.(newValue)
    setOpen(false)
  }

  // Click-outside on the root div
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <SelectContext.Provider value={{ value: currentValue, label, onValueChange: handleValueChange, open, setOpen }}>
      <div ref={rootRef} className="relative">
        {children}
      </div>
    </SelectContext.Provider>
  )
}

export interface SelectTriggerProps {
  children: React.ReactNode
  className?: string
}

export function SelectTrigger({ children, className }: SelectTriggerProps) {
  const { open, setOpen } = React.useContext(SelectContext)

  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      className={cn(
        'flex h-10 w-full items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-gray-100',
        'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent',
        'hover:border-white/20 hover:bg-white/8 transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
    >
      {children}
      <ChevronDown className={cn('h-4 w-4 text-gray-400 shrink-0 transition-transform', open && 'rotate-180')} />
    </button>
  )
}

export interface SelectValueProps {
  placeholder?: string
}

export function SelectValue({ placeholder }: SelectValueProps) {
  const { value, label } = React.useContext(SelectContext)
  return (
    <span className={cn('truncate', !value ? 'text-gray-500' : 'text-gray-100')}>
      {value ? label || value : (placeholder ?? 'Select...')}
    </span>
  )
}

export interface SelectContentProps {
  children: React.ReactNode
  className?: string
}

export function SelectContent({ children, className }: SelectContentProps) {
  const { open } = React.useContext(SelectContext)

  if (!open) return null

  return (
    <div
      className={cn(
        'absolute z-50 top-full mt-1 w-full rounded-lg border border-white/10 bg-gray-900 shadow-xl py-1 max-h-60 overflow-auto',
        className
      )}
    >
      {children}
    </div>
  )
}

export interface SelectItemProps {
  value: string
  children: React.ReactNode
  className?: string
}

export function SelectItem({ value, children, className }: SelectItemProps) {
  const { value: selectedValue, onValueChange } = React.useContext(SelectContext)
  const isSelected = selectedValue === value

  return (
    <div
      onMouseDown={(e) => {
        // Use onMouseDown + preventDefault to fire before the blur/outside-click
        e.preventDefault()
        onValueChange(value, typeof children === 'string' ? children : String(children))
      }}
      className={cn(
        'flex items-center justify-between px-3 py-2 text-sm cursor-pointer transition-colors',
        isSelected
          ? 'bg-indigo-500/20 text-indigo-300'
          : 'text-gray-300 hover:bg-white/5',
        className
      )}
    >
      {children}
      {isSelected && <Check className="h-4 w-4 shrink-0" />}
    </div>
  )
}
